import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../../lib/firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { ArrowLeft, BookOpen, CheckCircle, Clock, TrendingUp, Award, DollarSign } from "lucide-react";

interface CourseProgress {
  courseId: string;
  courseTitle: string;
  courseThumbnail: string;
  totalLessons: number;
  completedLessons: number;
  percentage: number;
  lastAccessDate: Date | null;
  lastLesson: string;
  paymentAmount: number;
}

export default function StudentDetail() {
  const { studentId } = useParams<{ studentId: string }>();
  const { user } = useAuthContext();
  const [student, setStudent] = useState<any>(null);
  const [courses, setCourses] = useState<CourseProgress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStudentDetail() {
      if (!user || !studentId) return;

      try {
        // Load student info
        const studentDoc = await getDoc(doc(db, "users", studentId));
        if (studentDoc.exists()) {
          setStudent({ id: studentId, ...studentDoc.data() });
        }

        // Get instructor's courses
        const coursesQuery = query(
          collection(db, "courses"),
          where("instructorId", "==", user.uid)
        );
        const coursesSnap = await getDocs(coursesQuery);
        const instructorCourses = coursesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const courseIds = instructorCourses.map(c => c.id);

        // Get payments for this student - only approved payments
        const paymentsQuery = query(
          collection(db, "payments"),
          where("userId", "==", studentId),
          where("status", "==", "completed")
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        
        // Create payment amount map by courseId - only approved payments
        const paymentAmountMap = new Map<string, number>();
        paymentsSnap.docs
          .filter(paymentDoc => paymentDoc.data().approvedBy)
          .forEach(paymentDoc => {
            const payment = paymentDoc.data();
            if (courseIds.includes(payment.courseId)) {
              paymentAmountMap.set(payment.courseId, Number(payment.amount || 0));
            }
          });

        // Get student's progress
        const progressQuery = query(
          collection(db, "progress"),
          where("userId", "==", studentId)
        );
        const progressSnap = await getDocs(progressQuery);

        // Group progress by course
        const courseProgressMap = new Map<string, any>();
        
        for (const progressDoc of progressSnap.docs) {
          const progressData = progressDoc.data();
          const courseId = progressData.courseId;

          // Only include instructor's courses
          if (!instructorCourses.find(c => c.id === courseId)) continue;

          if (!courseProgressMap.has(courseId)) {
            courseProgressMap.set(courseId, {
              completedLessons: [],
              lastAccessDate: null,
              lastLesson: ""
            });
          }

          const courseProgress = courseProgressMap.get(courseId);
          
          if (progressData.completed) {
            courseProgress.completedLessons.push(progressData.lessonId);
          }

          if (progressData.completedAt) {
            const accessDate = progressData.completedAt.toDate();
            if (!courseProgress.lastAccessDate || accessDate > courseProgress.lastAccessDate) {
              courseProgress.lastAccessDate = accessDate;
              
              // Get lesson title
              const lessonDoc = await getDoc(doc(db, "lessons", progressData.lessonId));
              if (lessonDoc.exists()) {
                courseProgress.lastLesson = lessonDoc.data().title || "ไม่ระบุ";
              }
            }
          }
        }

        // Build course progress list
        const courseProgressList: CourseProgress[] = [];

        for (const course of instructorCourses) {
          const courseProgress = courseProgressMap.get(course.id);
          
          if (!courseProgress) continue; // Student hasn't started this course

          // Get total lessons
          const lessonsQuery = query(
            collection(db, "lessons"),
            where("courseId", "==", course.id)
          );
          const lessonsSnap = await getDocs(lessonsQuery);
          const totalLessons = lessonsSnap.size;
          const completedLessons = courseProgress.completedLessons.length;
          const percentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

          courseProgressList.push({
            courseId: course.id,
            courseTitle: course.title || "ไม่ระบุชื่อ",
            courseThumbnail: course.thumbnail || "",
            totalLessons,
            completedLessons,
            percentage,
            lastAccessDate: courseProgress.lastAccessDate,
            lastLesson: courseProgress.lastLesson,
            paymentAmount: paymentAmountMap.get(course.id) || 0
          });
        }

        // Sort by last access (most recent first)
        courseProgressList.sort((a, b) => {
          if (!a.lastAccessDate) return 1;
          if (!b.lastAccessDate) return -1;
          return b.lastAccessDate.getTime() - a.lastAccessDate.getTime();
        });

        setCourses(courseProgressList);
        setLoading(false);
      } catch (error) {
        console.error("Error loading student detail:", error);
        setLoading(false);
      }
    }

    loadStudentDetail();
  }, [user, studentId]);

  const formatDate = (date: Date | null) => {
    if (!date) return "ไม่มีข้อมูล";
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const totalCompleted = courses.reduce((sum, c) => sum + c.completedLessons, 0);
  const totalLessons = courses.reduce((sum, c) => sum + c.totalLessons, 0);
  const overallProgress = totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0;
  const totalRevenue = courses.reduce((sum, c) => sum + c.paymentAmount, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-800 mb-4">ไม่พบข้อมูลนักเรียน</p>
          <Link to="/instructor/students">
            <button className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold">
              กลับไปหน้ารายชื่อ
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Link to="/instructor/students">
          <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold">
            <ArrowLeft className="w-5 h-5" />
            กลับไปหน้ารายชื่อ
          </button>
        </Link>

        {/* Student Profile Card */}
        <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {student.profileImage ? (
              <img
                src={student.profileImage}
                alt={student.name}
                className="w-32 h-32 rounded-full object-cover ring-4 ring-purple-200"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-600 to-pink-600 flex items-center justify-center text-white text-5xl font-bold">
                {student.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{student.name || "ไม่ระบุชื่อ"}</h1>
              <p className="text-gray-600 mb-4">{student.email}</p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="bg-blue-100 px-4 py-2 rounded-xl">
                  <p className="text-sm text-blue-600 font-semibold">คอร์สที่ลงทะเบียน</p>
                  <p className="text-2xl font-bold text-blue-700">{courses.length}</p>
                </div>
                <div className="bg-green-100 px-4 py-2 rounded-xl">
                  <p className="text-sm text-green-600 font-semibold">ความคืบหน้ารวม</p>
                  <p className="text-2xl font-bold text-green-700">{overallProgress}%</p>
                </div>
                <div className="bg-blue-100 px-4 py-2 rounded-xl">
                  <p className="text-sm text-blue-600 font-semibold">บทเรียนที่เสร็จ</p>
                  <p className="text-2xl font-bold text-blue-700">{totalCompleted}/{totalLessons}</p>
                </div>
                <div className="bg-yellow-100 px-4 py-2 rounded-xl">
                  <p className="text-sm text-yellow-600 font-semibold">ยอดชำระรวม</p>
                  <p className="text-2xl font-bold text-yellow-700">฿{totalRevenue.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">ความคืบหน้ารวม</h2>
          </div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-700 font-semibold">ทั้งหมด</span>
              <span className="text-3xl font-bold text-blue-600">{overallProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-6">
              <div
                className="bg-gradient-to-r from-blue-600 to-pink-600 h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                style={{ width: `${overallProgress}%` }}
              >
                {overallProgress > 10 && (
                  <span className="text-white text-sm font-bold">{overallProgress}%</span>
                )}
              </div>
            </div>
          </div>
          <p className="text-gray-600">
            เรียนจบแล้ว <span className="font-bold text-blue-600">{totalCompleted}</span> จาก{" "}
            <span className="font-bold">{totalLessons}</span> บทเรียน
          </p>
        </div>

        {/* Courses List */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-800">คอร์สที่ลงทะเบียน</h2>
          </div>

          {courses.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center shadow-xl">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">ยังไม่มีคอร์ส</h3>
              <p className="text-gray-600">นักเรียนยังไม่ได้ลงทะเบียนคอร์สใดๆ</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {courses.map((course) => (
                <div
                  key={course.courseId}
                  className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Course Thumbnail */}
                    <div className="md:w-64 h-48 md:h-auto">
                      <img
                        src={course.courseThumbnail || "https://via.placeholder.com/400x300?text=No+Image"}
                        alt={course.courseTitle}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Course Info */}
                    <div className="flex-1 p-6">
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">{course.courseTitle}</h3>

                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-700">ความคืบหน้า</span>
                          <span className="text-xl font-bold text-blue-600">{course.percentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div
                            className="bg-gradient-to-r from-blue-600 to-pink-600 h-4 rounded-full transition-all duration-500"
                            style={{ width: `${course.percentage}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {course.completedLessons}/{course.totalLessons} บทเรียน
                        </p>
                      </div>

                      {/* Last Activity */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                          <Clock className="w-5 h-5 text-blue-600 mt-1" />
                          <div>
                            <p className="text-sm text-gray-600 mb-1">เข้าเรียนล่าสุด</p>
                            <p className="text-sm font-semibold text-gray-800">
                              {formatDate(course.lastAccessDate)}
                            </p>
                          </div>
                        </div>

                        {course.lastLesson && (
                          <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-1" />
                            <div>
                              <p className="text-sm text-gray-600 mb-1">บทล่าสุด</p>
                              <p className="text-sm font-semibold text-gray-800 line-clamp-2">
                                {course.lastLesson}
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-3 bg-green-50 p-4 rounded-xl">
                          <DollarSign className="w-5 h-5 text-green-600 mt-1" />
                          <div>
                            <p className="text-sm text-green-600 mb-1">ยอดชำระ</p>
                            <p className="text-lg font-bold text-green-700">
                              ฿{course.paymentAmount.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Completion Badge */}
                      {course.percentage === 100 && (
                        <div className="mt-4 flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-xl w-fit">
                          <Award className="w-5 h-5" />
                          <span className="font-semibold">เรียนจบแล้ว</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
